import { DocumentAddIcon, XIcon } from "@heroicons/react/outline";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import LoadingSpinner from "../../../components/LoadingSpinner";
import DropZone from "../../../components/Playground/Messages/Message/MediaOptions/MediaUploader/DropZone";
import { Button } from "../../../components/ui/button";

import { useApiKeyStore } from "./store/api-key-store";
import { VertexAiApiKeys } from "./types";
import { RevealableInput } from "./RevealableInput";

import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/auth-context";
import { useSetOrRemoveVertexAIKeys } from "@/queries/apiKeys";

const VertexAiFileDisplay: React.FC<{
  file: File | null;
  hasApiKey: boolean;
  onRemove: () => void;
  onDrop: (file: File) => void;
  onOpenFilePicker: () => void;
}> = ({ file, hasApiKey, onRemove, onDrop, onOpenFilePicker }) => {
  if (file) {
    return (
      <div className="flex h-10 w-full items-center justify-between gap-2 rounded-md border border-gray-200 px-2 py-0.5 text-sm text-gray-700">
        {file.name}
        <Button variant="ghost" size="tinyIcon" onClick={onRemove}>
          <XIcon className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    );
  }

  if (hasApiKey) {
    return (
      <div className="flex h-10 w-full items-center justify-center rounded-md border border-gray-200 px-2 py-0.5 text-sm text-gray-700">
        Credentials file saved
      </div>
    );
  }

  return (
    <DropZone
      onDrop={onDrop}
      onClick={onOpenFilePicker}
      isLoading={false}
      uploadIcon={DocumentAddIcon}
    >
      <span className="text-xs text-gray-500">Drop file or click to upload</span>
    </DropZone>
  );
};

const LoadingSkeleton = () => (
  <div className="max-w-[600px]">
    <div className="mt-1 flex flex-col gap-1">
      <div className="flex flex-col gap-1">
        <div className="flex w-full flex-col gap-1">
          <Skeleton className="h-5 w-full max-w-[120px]" />
          <Skeleton className="h-9 w-full max-w-[600px]" />
        </div>
        <div className="flex w-full flex-col gap-1">
          <Skeleton className="h-5 w-full max-w-[120px]" />
          <Skeleton className="h-9 w-full max-w-[600px]" />
        </div>
      </div>
      <div className="mt-2 flex max-w-[500px] flex-col gap-2">
        <Skeleton className="h-5 w-full max-w-[250px]" />
        <Skeleton className="h-24 w-full max-w-[500px]" />
      </div>
      <div className="mt-2 flex w-full">
        <Skeleton className="h-10 w-[210px]" />
      </div>
    </div>
  </div>
);

interface VertexAISectionProps {
  onAfterSaveSuccess?: () => void;
}

const VertexAISection = observer(({ onAfterSaveSuccess }: VertexAISectionProps) => {
  const auth = useAuth();
  const { workspaceId } = useParams();
  const store = useApiKeyStore();
  const hasVertexAiApiKey = !!store.getApiKey("vertexai");
  const isStoreLoading = store.isLoading;

  const [vertexAiApiKey, setVertexAiApiKey] = useState<VertexAiApiKeys>({
    vertexai_project: "",
    vertexai_location: "",
    vertexai_security_credentials: "",
  });
  const [vertexAiFile, setVertexAiFile] = useState<File | null>(null);

  const { mutateAsync, isPending } = useSetOrRemoveVertexAIKeys(auth?.userToken ?? "");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const saveVertexAiKeys = async (vertexAiKeys: VertexAiApiKeys | null) => {
    const response = await mutateAsync({
      ...(vertexAiKeys
        ? vertexAiKeys
        : {
            vertexai_project: "",
            vertexai_location: "",
            vertexai_security_credentials: "{}",
          }),
      workspace_id: Number(workspaceId),
    });
    if (response.ok) {
      store.setApiKey("vertexai", !!vertexAiKeys);
      store.setApiKeyConfig("vertexai_project", vertexAiKeys?.vertexai_project ?? "");
      store.setApiKeyConfig("vertexai_location", vertexAiKeys?.vertexai_location ?? "");
      setVertexAiApiKey({
        vertexai_project: "",
        vertexai_location: "",
        vertexai_security_credentials: "",
      });
      setVertexAiFile(null);
      if (vertexAiKeys) {
        onAfterSaveSuccess?.();
      }
    }
  };

  const removeApiKey = async () => {
    await saveVertexAiKeys(null);
  };

  const handleDrop = (file: File) => {
    setVertexAiFile(file);
  };

  useEffect(() => {
    if (vertexAiFile) {
      const reader = new FileReader();
      reader.onload = () => {
        setVertexAiApiKey(prev => ({
          ...prev,
          vertexai_security_credentials: reader.result as string,
        }));
      };
      reader.readAsText(vertexAiFile);
    }
  }, [vertexAiFile, setVertexAiApiKey]);

  if (isStoreLoading) {
    return <LoadingSkeleton />;
  }

  const isFormValid =
    vertexAiApiKey.vertexai_project &&
    vertexAiApiKey.vertexai_location &&
    vertexAiApiKey.vertexai_security_credentials;

  return (
    <div className="mt-2 flex h-full flex-col">
      <div className="mt-1 flex max-w-[600px] flex-col gap-2">
        <div className="flex flex-col gap-2">
          <RevealableInput
            label="Vertex AI Project"
            name="vertexai_project"
            id="vertexai_project"
            placeholder="Enter Vertex AI Project..."
            value={
              hasVertexAiApiKey
                ? store.getApiKeyConfig("vertexai_project")
                : vertexAiApiKey.vertexai_project
            }
            readOnly={hasVertexAiApiKey}
            onChange={e =>
              setVertexAiApiKey({
                ...vertexAiApiKey,
                vertexai_project: e.target.value,
              })
            }
          />
          <RevealableInput
            label="Vertex AI Location"
            name="vertexai_location"
            id="vertexai_location"
            placeholder="Enter Vertex AI Location..."
            value={
              hasVertexAiApiKey
                ? store.getApiKeyConfig("vertexai_location")
                : vertexAiApiKey.vertexai_location
            }
            readOnly={hasVertexAiApiKey}
            onChange={e =>
              setVertexAiApiKey({
                ...vertexAiApiKey,
                vertexai_location: e.target.value,
              })
            }
          />
        </div>
        <div className="flex max-w-[500px] flex-col">
          <label htmlFor="vertexai_security_credentials_file" className="text-sm text-gray-600">
            Attach Vertex AI Security Credentials JSON file
          </label>

          <div>
            <VertexAiFileDisplay
              file={vertexAiFile}
              hasApiKey={hasVertexAiApiKey}
              onRemove={() => {
                setVertexAiFile(null);
                setVertexAiApiKey({
                  ...vertexAiApiKey,
                  vertexai_security_credentials: "",
                });
              }}
              onDrop={handleDrop}
              onOpenFilePicker={() => fileInputRef.current?.click()}
            />
            <input
              ref={fileInputRef}
              id="vertexai_security_credentials_file"
              type="file"
              className="hidden"
              accept=".json"
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) {
                  handleDrop(file);
                }
              }}
            />
          </div>
        </div>

        <div className="mt-2">
          {!hasVertexAiApiKey ? (
            <Button
              onClick={() => saveVertexAiKeys(vertexAiApiKey)}
              disabled={!isFormValid || isPending}
              variant="outline"
            >
              {isPending ? <LoadingSpinner size={5} /> : "Save Vertex AI Credentials"}
            </Button>
          ) : (
            <Button variant="destructiveOutline" onClick={removeApiKey} disabled={isPending}>
              Remove Vertex AI Credentials
            </Button>
          )}
        </div>
      </div>
    </div>
  );
});

export default VertexAISection;
